"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, HeartHandshake, BookOpen, Sparkles, ArrowUpRight, Globe as Globe2, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const missionPillars = [{
  title: "Evangelizar a los Perdidos",
  subtitle: "Alcanzando a Cada Nación",
  tagline: "Proclamando a Cristo Jesús en todo el mundo",
  icon: Globe2,
  verse: "Marcos 16:15",
  description: "Movilizando a creyentes e iglesias en todo el mundo para compartir la esperanza del Evangelio en cada comunidad y cultura."
}, {
  title: "Adorar a Dios",
  subtitle: "Adoración de Corazón",
  tagline: "Exaltando al Creador en Espíritu y Verdad",
  icon: Flame,
  verse: "Juan 4:24",
  description: "Cultivando encuentros de adoración vibrantes y llenos del Espíritu que honran a Dios y transforman corazones."
}, {
  title: "Discipular a los Creyentes",
  subtitle: "Madurez Espiritual",
  tagline: "Equipando a los seguidores para una fe duradera",
  icon: BookOpen,
  verse: "Mateo 28:19-20",
  description: "Guiando a los creyentes mediante el estudio bíblico, el compañerismo y el discipulado hacia la madurez en Cristo."
}, {
  title: "Mostrar Compasión",
  subtitle: "Misericordia Viva",
  tagline: "Atendiendo necesidades reales con el amor de Cristo",
  icon: HeartHandshake,
  verse: "Gálatas 2:10",
  description: "Respondiendo ante la necesidad humana a través de ayuda humanitaria, atención comunitaria y servicio práctico."
}];
const marqueeItems = ["Evangelizar a los Perdidos", "•", "Adorar a Dios", "•", "Discipular a los Creyentes", "•", "Mostrar Compasión", "•", "Compañerismo en el Espíritu", "•", "Plantación Global de Iglesias", "•", "Pueblos No Alcanzados", "•", "Transformando Comunidades", "•"];
export default function Ribbon() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="missionribbon" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-y border-border">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden="true">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-3xl bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-3xl bg-secondary/15 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/30 text-primary bg-primary/5">
              <Sparkles className="size-3 text-primary" />
              Nuestro Cuádruple Propósito
            </Badge>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">La Misión de las <br />Asambleas de Dios</h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Como confraternidad global, nuestro llamado se centra en cuatro mandatos bíblicos: alcanzar a los no alcanzados, exaltar a Dios en adoración, formar discípulos y extender un servicio compasivo.
          </p>
        </div>

        {}
       <div className="relative w-screen max-w-none ml-[calc(50%-50vw)] my-8 overflow-hidden bg-primary text-primary-foreground py-4 sm:py-5 shadow-md">
  <div className="flex w-full select-none overflow-hidden">
    <motion.div className="flex shrink-0 items-center gap-6 whitespace-nowrap text-sm sm:text-base md:text-lg font-semibold tracking-wide uppercase" animate={shouldReduceMotion ? {
            x: 0
          } : {
            x: ["0%", "-50%"]
          }} transition={shouldReduceMotion ? {
            duration: 0
          } : {
            repeat: Infinity,
            ease: "linear",
            duration: 25
          }}>
      {[...marqueeItems, ...marqueeItems].map((text, i) => <span key={i} data-index={i} className={text === "•" ? "text-primary-foreground/50 font-black text-xl px-2" : "tracking-widest"}>
          {text}
        </span>)}
    </motion.div>
  </div>
      </div>

        {}
        <div className="mt-12 md:mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {missionPillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <div key={pillar.title} data-index={i} className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-6 md:p-7 shadow-xs hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-300">
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-[color,background-color] duration-300">
                      <IconComponent className="size-6" />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-md">
                      {pillar.verse}
                    </span>
                  </div>

                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {pillar.subtitle}
                  </span>
                  <h3 className="mt-1 text-xl font-bold text-card-foreground">
                    {pillar.title}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-normal">
                    {pillar.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    {pillar.tagline}
                  </span>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-12 md:mt-16 rounded-2xl bg-muted/40 border border-border p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h4 className="text-lg md:text-xl font-semibold text-foreground">
              Descubra Nuestros Ministerios en Todo el Mundo
            </h4>
            <p className="text-sm text-muted-foreground">
              Conozca cómo nuestros ministerios globales dan vida a estos principios fundamentales cada día.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild variant="default" size="lg" className="rounded-lg shadow-sm">
              <Link to="/MinistriesPage">
                Explorar Ministerios
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-lg">
              <Link to="/HomePage">
                Volver al Inicio
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}